// One-click Penelope on a Value Dashboard game row (Doom's Update 01 PEN-09 / PEN-01).
//
// START applies the caller's saved default with BOTH side permissions (POST /api/penelope/games/start — idempotent on
// the relay: a running game is left untouched, one stopped with STOP resumes with its own settings). STOP disarms
// that game only: her working orders are cancelled, her inventory stays, nothing is liquidated, manual orders and her
// other games are untouched. There is no confirm dialog here — a LIVE default is confirmed once, when it is saved
// (Josh 2026-10-12) — so the chip always says LIVE or PRACTICE before the click.
//
// A sport without a saved default of its own never starts from the all-sports default silently (Handoff 03 PEN-08,
// 2026-10-15): the relay answers PENELOPE_NO_SPORT_PROFILE and the row offers the two answers — start from the
// all-sports default this once, or save a copy of it as this sport's default (then every row of that sport starts).
//
// Lives in the Date cell beside SaveGameToggle, with the same click isolation (the row's onClick expands it).
// Every row reads one shared poll (usePenelopeMine), never a read of its own.

import { useMemo, useState, type MouseEvent } from 'react';
import { Loader2, Play, Square } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { apiFetch } from '@/stores/authStore';
import { penelopeAttachInput } from '@/hooks/usePenelope';
import { usePenelopeAccess, usePenelopeServiceOn } from '@/hooks/usePenelopeAccess';
import { refreshPenelopeMine, usePenelopeMine, usePenelopeMineStore, type PenelopeStopState } from '@/hooks/usePenelopeMine';
import { PENELOPE_EVENT_PREFIXES, PENELOPE_SPORT_LABEL, penelopeSportOfEvent } from '@/hooks/usePenelopeSides';
import type { GameRow } from './types';

const STOP_LABEL: Record<PenelopeStopState['status'], string> = {
  stopping: 'stopping', 'cancels-pending': 'cancels pending', clean: 'stopped', unconfirmed: 'unconfirmed',
};

/** The relay's refusal in the trader's words: the code's sentence without the code. */
export function penelopeRefusal(error: string): string {
  const m = /^(?:conflict|forbidden): ([A-Z_]+)(?: \((.*)\))?$/s.exec(error.trim());
  if (!m) return error;
  const [, code, detail] = m;
  const plain: Record<string, string> = {
    PENELOPE_NO_PROFILE: 'save your Penelope default first (open the game → Penelope → Save as My Default)',
    PENELOPE_NO_SPORT_PROFILE: detail ?? 'no default saved for this sport',
    PENELOPE_LIVE_LOCKED: 'a manager set you to practice only',
    PENELOPE_LIVE_NOT_ALLOWED: 'live orders are off on this box',
    PENELOPE_LOCKED: 'a manager has switched Penelope off for you',
  };
  return plain[code!] ?? detail ?? code!;
}

export function PenelopeRowControl({ game }: { game: GameRow }) {
  const access = usePenelopeAccess();
  const serviceOn = usePenelopeServiceOn(access);
  const target = useMemo(() => penelopeAttachInput(game), [game]);
  const eligible = access && serviceOn && target !== null && PENELOPE_EVENT_PREFIXES.some((p) => target.eventTicker.startsWith(p));
  usePenelopeMine(eligible);
  const ev = target?.eventTicker ?? '';
  const loaded = usePenelopeMineStore((s) => s.data !== null);
  const g = usePenelopeMineStore((s) => (ev ? s.data?.games[ev] ?? null : null));
  const last = usePenelopeMineStore((s) => (ev ? s.data?.lastStopped[ev] ?? null : null));
  // The default this row would start from: its sport's own, else the all-sports one (2026-10-14).
  const sport = penelopeSportOfEvent(ev);
  const profile = usePenelopeMineStore((s) => (sport ? s.data?.profiles?.[sport] : undefined) ?? s.data?.profile ?? null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** The relay asked which default to start from (no default saved for this sport). */
  const [ask, setAsk] = useState<{ startMode: 'live' | 'shadow' } | null>(null);
  if (!eligible || !loaded || !target) return null;

  const running = g?.armed === true;
  const mode: 'live' | 'shadow' | null = g ? g.mode : profile?.startMode ?? null;
  const stop = g && !running ? g.stop : !g ? last?.stop ?? null : null;
  const matchup = `${game.awayCode} @ ${game.homeCode}`;
  const noDefault = !g && !profile;

  const click = async (e: MouseEvent, answer: 'useAllSportsDefault' | 'copyAllSportsDefault' | null = null) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    if (noDefault) { setProblem(penelopeRefusal('conflict: PENELOPE_NO_PROFILE')); return; }
    setBusy(true);
    setProblem(null);
    setAsk(null);
    try {
      const res = running
        ? await apiFetch(`/api/penelope/games/${g!.id}/disarm`, { method: 'POST' })
        : await apiFetch('/api/penelope/games/start', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(answer ? { ...target, [answer]: true } : target) });
      const body = (await res.json().catch(() => ({}))) as { error?: string; fallbackStartMode?: 'live' | 'shadow' };
      if (!res.ok) {
        if (res.status === 409 && /PENELOPE_NO_SPORT_PROFILE/.test(body.error ?? '')) setAsk({ startMode: body.fallbackStartMode ?? profile?.startMode ?? 'shadow' });
        else setProblem(penelopeRefusal(body.error || `HTTP ${res.status}`));
      }
    } catch (err) {
      setProblem((err as Error).message);
    } finally {
      await refreshPenelopeMine();
      setBusy(false);
    }
  };

  const regime = g?.running?.regime ?? null;
  const state = problem ? 'refused' : g?.parked ? 'blocked' : running ? (regime ? regime.toLowerCase() : 'starting') : stop && stop.status !== 'clean' ? STOP_LABEL[stop.status] : null;
  const stateTitle = problem ?? g?.parked
    ?? (running && g?.running ? `${regime}${g.running.gate ? ` — ${g.running.gate}` : ''} · net ${g.running.net} · P&L $${(g.running.pnlCents / 100).toFixed(2)}` : null)
    ?? (stop ? `last stop (${stop.reason}): ${STOP_LABEL[stop.status]}${stop.openAtLastCheck ? ` — ${stop.openAtLastCheck} order(s) still resting at the last check` : ''}${stop.unknownAtExit ? ` — ${stop.unknownAtExit} place(s) of unknown outcome` : ''}${stop.sweepFailed ? ' — the exit sweep failed' : ''}` : null)
    ?? undefined;
  const title = noDefault
    ? `Penelope: ${penelopeRefusal('conflict: PENELOPE_NO_PROFILE')}`
    : running
      ? `STOP Penelope on ${matchup}: cancels her orders here, keeps her inventory, touches nothing else`
      : `Start Penelope on ${matchup} with your saved default, both sides — ${mode === 'live' ? 'LIVE: real post-only orders on Kalshi' : 'practice (simulated)'}${g ? ' (resumes this game\'s own settings)' : ''}`;

  const sportLabel = sport ? PENELOPE_SPORT_LABEL[sport] : 'this sport';
  const answerBtn = 'rounded border border-amber-500/60 px-1 py-px font-mono text-[8px] uppercase leading-none text-amber-600 hover:bg-amber-500/15';
  return (
    <span className="flex items-center gap-0.5" data-testid="penelope-row-control">
      <button
        type="button"
        onClick={(e) => void click(e)}
        disabled={busy}
        aria-label={running ? `Stop Penelope on ${matchup}` : `Start Penelope on ${matchup}`}
        aria-pressed={running}
        title={title}
        className={cn(
          'shrink-0 rounded px-0.5 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
          noDefault ? 'text-muted-foreground/30 hover:text-muted-foreground'
            : running ? 'text-foreground' : mode === 'live' ? 'text-red-500/70 hover:text-red-500' : 'text-sky-500/70 hover:text-sky-500',
        )}
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : running ? <Square size={10} className="fill-current" /> : <Play size={11} />}
      </button>
      {(g || problem) && mode && (
        <span className={cn('rounded px-0.5 font-mono text-[8px] font-bold uppercase leading-none', mode === 'live' ? 'bg-red-500/15 text-red-500' : 'bg-sky-500/15 text-sky-500')}>
          {mode === 'live' ? 'live' : 'prac'}
        </span>
      )}
      {ask && !running && (
        <span role="alert" className="flex items-center gap-0.5" title={`No ${sportLabel} default saved. Start from your all-sports default (${ask.startMode === 'live' ? 'LIVE' : 'practice'}) this once, or save a copy of it as your ${sportLabel} default first — then every ${sportLabel} row starts from that copy. Or open the game → Penelope → Save as My ${sportLabel} Default.`}>
          <span className="font-mono text-[8px] uppercase leading-none text-amber-600">no {sportLabel} default</span>
          <button type="button" disabled={busy} onClick={(e) => void click(e, 'useAllSportsDefault')} className={answerBtn} aria-label={`Start ${matchup} from the all-sports default this once`}>all-sports once</button>
          <button type="button" disabled={busy} onClick={(e) => void click(e, 'copyAllSportsDefault')} className={answerBtn} aria-label={`Save a copy of the all-sports default as the ${sportLabel} default and start ${matchup}`}>copy → {sportLabel}</button>
        </span>
      )}
      {state && !ask && (
        <span
          title={stateTitle}
          role={problem || g?.parked || stop?.status === 'unconfirmed' ? 'alert' : undefined}
          className={cn('max-w-[5.5rem] truncate font-mono text-[8px] uppercase leading-none',
            problem || g?.parked || stop?.status === 'unconfirmed' ? 'text-red-500' : stop && !running ? 'text-amber-500' : 'text-muted-foreground')}
        >
          {state}
        </span>
      )}
    </span>
  );
}
